import React, { useEffect, useMemo, useRef, useState } from "react";
import { Hash, AtSign, Bug, Video, Terminal, Sparkles, Ticket, Users, Search, MessageSquare, CornerDownLeft } from "lucide-react";
import { api } from "@/lib/api";
import { dateTime } from "@/lib/dates";

const MAX_PER_GROUP = 5;

const matches = (haystack, needle) =>
  !needle || String(haystack || "").toLowerCase().includes(needle);

/**
 * Cmd+K palette over the workspace: channels, DMs, quick commands, tickets and
 * members. Search is instant — the lists are already in memory, so debouncing
 * would only add latency.
 *
 * The parent owns every action; this component only decides what is offered.
 */
export default function CommandPalette({
  open,
  onClose,
  channels = [],
  users = [],
  tickets = [],
  opsCommands = [],
  canCall = false,
  onSelectChannel,
  onSelectDm,
  onOpenMembers,
  onOpenTicket,
  onReportBug,
  onStartCall,
  onRunOpsCommand,
  onOpenAI,
  workspaceId,
}) {
  const [query, setQuery] = useState("");
  // Mesajele nu sunt în memoria clientului, deci se caută pe server. Debounce,
  // fiindcă asta e singura parte a paletei care traversează rețeaua — restul
  // rămâne instant, cum era.
  const [msgResults, setMsgResults] = useState([]);
  const [active, setActive] = useState(0);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setQuery("");
    setMsgResults([]);
    setActive(0);
    // Autofocus has to wait for the overlay to mount.
    const id = requestAnimationFrame(() => inputRef.current?.focus());
    return () => cancelAnimationFrame(id);
  }, [open]);

  const raw = query.trim();
  const q = raw.toLowerCase();
  const isCommand = q.startsWith("/");

  // Istoricul nu e în client, deci mesajele se caută pe server. 200 ms după ce
  // s-a oprit tastarea, și doar de la două caractere: o cerere pe literă ar
  // însemna o interogare pentru fiecare apăsare, pe o căutare care nu e instant.
  useEffect(() => {
    if (!open || !workspaceId || isCommand || raw.length < 2) {
      setMsgResults([]);
      return undefined;
    }
    let alive = true;
    const timer = setTimeout(async () => {
      try {
        const { data } = await api.get("/search/messages", {
          params: { workspace_id: workspaceId, q: raw, limit: 5 },
        });
        if (alive) setMsgResults(data?.results || []);
      } catch {
        if (alive) setMsgResults([]);
      }
    }, 200);
    return () => { alive = false; clearTimeout(timer); };
  }, [open, workspaceId, raw, isCommand]);
  // Everything after "/claude" or "/run" is an argument, not a filter.
  const argFor = (prefix) => (q.startsWith(prefix) ? raw.slice(prefix.length).trim() : "");

  const groups = useMemo(() => {
    const out = [];

    const commands = [];
    if (!isCommand || matches("/bug raporteaza un bug raportează", q.slice(1))) {
      commands.push({
        id: "cmd:bug", icon: Bug, label: "/bug", hint: "Raportează un bug",
        run: () => onReportBug?.(),
      });
    }
    if (canCall && (!isCommand || matches("/call apel video", q.slice(1)))) {
      commands.push({
        id: "cmd:call", icon: Video, label: "/call", hint: "Pornește un apel în conversația curentă",
        run: () => onStartCall?.(),
      });
    }
    if (opsCommands.length > 0) {
      const arg = argFor("/run");
      if (q.startsWith("/run")) {
        // Drilled into /run: offer the whitelist itself.
        opsCommands
          .filter((c) => matches(c, arg.toLowerCase()))
          .slice(0, MAX_PER_GROUP)
          .forEach((c) => commands.push({
            id: `ops:${c}`, icon: Terminal, label: c, hint: "#ops",
            run: () => onRunOpsCommand?.(c),
          }));
      } else if (!isCommand || matches("/run comenzi ops", q.slice(1))) {
        commands.push({
          id: "cmd:run", icon: Terminal, label: "/run", hint: "Comenzi #ops",
          fill: "/run ",
        });
      }
    }
    if (!isCommand || matches("/claude vibe ai", q.slice(1))) {
      const prompt = argFor("/claude");
      commands.push({
        id: "cmd:claude", icon: Sparkles, label: "/claude",
        hint: prompt ? `Întreabă VIBE.AI: ${prompt}` : "Deschide VIBE.AI",
        run: () => onOpenAI?.(prompt),
      });
    }
    // A "/" query is only ever about commands.
    if (isCommand) {
      if (commands.length) out.push({ title: "COMENZI", items: commands.slice(0, MAX_PER_GROUP) });
      return out;
    }

    const chanQuery = q.replace(/^#/, "");
    const chans = channels
      .filter((c) => matches(c.name, chanQuery))
      .slice(0, MAX_PER_GROUP)
      .map((c) => ({
        id: `chan:${c.id}`, icon: Hash, label: c.name, hint: c.description || "",
        run: () => onSelectChannel?.(c.id),
      }));
    if (chans.length) out.push({ title: "CANALE", items: chans });

    const userQuery = q.replace(/^@/, "");
    const dms = users
      .filter((u) => matches(u.name, userQuery) || matches(u.email, userQuery))
      .slice(0, MAX_PER_GROUP)
      .map((u) => ({
        // Emailul, nu „Mesaj direct". Doi colegi cu același nume erau două
        // rânduri identice, iar alegerea era dată cu banul — aceeași greșeală
        // ca la lista de fișiere, unde șapte `snip1.png` arătau la fel.
        id: `dm:${u.id}`, icon: AtSign, label: u.name,
        hint: u.email || "Mesaj direct",
        run: () => onSelectDm?.(u.id),
      }));
    if (dms.length) out.push({ title: "MESAJE DIRECTE", items: dms });

    if (commands.length) out.push({ title: "COMENZI", items: commands.slice(0, MAX_PER_GROUP) });

    const tix = tickets
      .filter((t) => matches(t.title, q))
      .slice(0, MAX_PER_GROUP)
      .map((t) => ({
        id: `ticket:${t.id}`, icon: Ticket, label: t.title,
        hint: [t.custom_status || t.status, t.app].filter(Boolean).join(" · "),
        run: () => onOpenTicket?.(t.id),
      }));
    if (tix.length) out.push({ title: "TICHETE", items: tix });

    const members = users
      .filter((u) => matches(u.name, userQuery) || matches(u.email, userQuery))
      .slice(0, MAX_PER_GROUP)
      .map((u) => ({
        id: `member:${u.id}`, icon: Users, label: u.name, hint: u.email || "",
        run: () => onOpenMembers?.(),
      }));
    if (members.length) out.push({ title: "MEMBRI", items: members });

    const msgs = msgResults.slice(0, MAX_PER_GROUP).map((m) => ({
      id: `msg:${m.id}`, icon: MessageSquare,
      label: m.content.replace(/\s+/g, " ").slice(0, 90),
      // Cine și unde, ca două rezultate din același canal să nu arate la fel.
      hint: [m.sender_name, m.channel_name ? `#${m.channel_name}` : "mesaj direct",
             dateTime(m.created_at)].filter(Boolean).join(" · "),
      run: () => (m.channel_id ? onSelectChannel?.(m.channel_id)
                               : onSelectDm?.(m.sender_id)),
    }));
    if (msgs.length) out.push({ title: "MESAJE", items: msgs });

    return out;
  }, [
    q, raw, isCommand, channels, users, tickets, opsCommands, canCall, msgResults,
    onSelectChannel, onSelectDm, onOpenMembers, onOpenTicket,
    onReportBug, onStartCall, onRunOpsCommand, onOpenAI,
  ]);

  const flat = useMemo(() => groups.flatMap((g) => g.items), [groups]);

  useEffect(() => { setActive(0); }, [q]);
  useEffect(() => {
    if (active >= flat.length) setActive(flat.length ? flat.length - 1 : 0);
  }, [flat.length, active]);

  useEffect(() => {
    listRef.current?.querySelector('[data-active="true"]')?.scrollIntoView({ block: "nearest" });
  }, [active, flat.length]);

  if (!open) return null;

  const choose = (item) => {
    if (!item) return;
    if (item.fill) {          // a two-step entry: refine the query instead of acting
      setQuery(item.fill);
      inputRef.current?.focus();
      return;
    }
    onClose?.();
    item.run?.();
  };

  const onKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((i) => (flat.length ? (i + 1) % flat.length : 0));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((i) => (flat.length ? (i - 1 + flat.length) % flat.length : 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      choose(flat[active]);
    } else if (e.key === "Escape") {
      e.preventDefault();
      onClose?.();
    }
  };

  let index = -1;
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center p-4 pt-[12vh]">
      <button
        type="button"
        aria-label="Închide paleta de comenzi"
        onClick={onClose}
        className="absolute inset-0 bg-black/60"
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Paletă de comenzi"
        data-testid="command-palette"
        className="relative w-full max-w-xl bg-surface border border-line/20 shadow-2xl flex flex-col max-h-[70vh]"
      >
        <div className="flex items-center gap-2 px-4 h-12 border-b border-line/10 flex-shrink-0">
          <Search className="w-4 h-4 text-fg-muted flex-shrink-0" />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onKeyDown}
            data-testid="command-palette-input"
            placeholder="Caută canale, oameni, tichete, mesaje — sau / pentru comenzi"
            className="flex-1 bg-transparent text-sm text-fg outline-none placeholder-fg/30"
          />
          <kbd className="label-tiny text-fg-muted border border-line/20 px-1.5 py-0.5 flex-shrink-0">ESC</kbd>
        </div>

        <div ref={listRef} className="flex-1 overflow-y-auto py-2">
          {flat.length === 0 ? (
            <p className="px-4 py-8 text-center text-sm text-fg-muted">Niciun rezultat.</p>
          ) : groups.map((g) => (
            <div key={g.title} className="mb-1">
              <p className="label-tiny px-4 py-1.5 text-fg-muted">// {g.title}</p>
              {g.items.map((item) => {
                index += 1;
                const i = index;
                const Icon = item.icon;
                const isActive = i === active;
                return (
                  <button
                    key={item.id}
                    type="button"
                    data-active={isActive}
                    data-testid={`command-palette-item-${item.id}`}
                    onMouseEnter={() => setActive(i)}
                    onClick={() => choose(item)}
                    className={`w-full px-4 py-2 flex items-center gap-3 text-left transition-colors ${
                      isActive ? "bg-surface-2 text-fg" : "text-fg/80 hover:bg-surface-2/60"
                    }`}
                  >
                    <Icon className={`w-3.5 h-3.5 flex-shrink-0 ${isActive ? "text-brand" : "text-fg-muted"}`} />
                    <span className="text-sm truncate">{item.label}</span>
                    {item.hint && <span className="text-xs text-fg-muted truncate ml-auto pl-3">{item.hint}</span>}
                    {isActive && <CornerDownLeft className="w-3 h-3 text-fg-muted flex-shrink-0" />}
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
